import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type MekkeBookletResponseDto,
  type MekkeBookletDetailResponseDto,
  type CreateMekkeBookletDto,
  type UpdateMekkeBookletDto,
  type MekkeBookletSpotDto,
} from "@tabipla/shared";

// Query key factory
const bookletKeys = {
  all: ["mekke-booklets"] as const,
  lists: () => [...bookletKeys.all, "list"] as const,
  list: () => [...bookletKeys.lists()] as const,
  details: () => [...bookletKeys.all, "detail"] as const,
  detail: (id: string) => [...bookletKeys.details(), id] as const,
  shared: (shareCode: string) => [...bookletKeys.all, "shared", shareCode] as const,
};

// Fetch all booklets for the current user
export function useBooklets() {
  return useQuery({
    queryKey: bookletKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<MekkeBookletResponseDto[]>("/api/mekke/booklets");
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch booklets");
      }
      return response.data ?? [];
    },
  });
}

// Fetch single booklet with spots
export function useBooklet(bookletId: string | undefined) {
  return useQuery({
    queryKey: bookletKeys.detail(bookletId ?? ""),
    queryFn: async () => {
      if (!bookletId) throw new Error("Booklet ID is required");
      const response = await apiClient.get<MekkeBookletDetailResponseDto>(
        `/api/mekke/booklets/${bookletId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch booklet");
      }
      return response.data;
    },
    enabled: !!bookletId,
  });
}

// Fetch shared booklet by share code
export function useSharedBooklet(shareCode: string | undefined) {
  return useQuery({
    queryKey: bookletKeys.shared(shareCode ?? ""),
    queryFn: async () => {
      if (!shareCode) throw new Error("Share code is required");
      const response = await apiClient.get<MekkeBookletDetailResponseDto>(
        `/api/mekke/booklets/shared/${shareCode}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch shared booklet");
      }
      return response.data;
    },
    enabled: !!shareCode,
  });
}

// Create booklet
export function useCreateBooklet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateMekkeBookletDto) => {
      const response = await apiClient.post<MekkeBookletResponseDto>("/api/mekke/booklets", input);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create booklet");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.all });
    },
  });
}

// Update booklet
export function useUpdateBooklet(bookletId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateMekkeBookletDto) => {
      const response = await apiClient.put<MekkeBookletResponseDto>(
        `/api/mekke/booklets/${bookletId}`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update booklet");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.all });
      queryClient.invalidateQueries({ queryKey: bookletKeys.detail(bookletId) });
    },
  });
}

// Delete booklet
export function useDeleteBooklet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookletId: string) => {
      const response = await apiClient.delete<{ id: string }>(`/api/mekke/booklets/${bookletId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete booklet");
      }
      return bookletId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.all });
    },
  });
}

// Add spot to booklet
interface AddSpotToBookletInput {
  spotId: string;
  comment?: string;
}

export function useAddSpotToBooklet(bookletId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AddSpotToBookletInput) => {
      const response = await apiClient.post<MekkeBookletSpotDto>(
        `/api/mekke/booklets/${bookletId}/spots`,
        input
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to add spot to booklet");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.detail(bookletId) });
      queryClient.invalidateQueries({ queryKey: bookletKeys.all });
    },
  });
}

// Remove spot from booklet
export function useRemoveSpotFromBooklet(bookletId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotId: string) => {
      const response = await apiClient.delete<{ spotId: string }>(
        `/api/mekke/booklets/${bookletId}/spots/${spotId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to remove spot from booklet");
      }
      return spotId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.detail(bookletId) });
      queryClient.invalidateQueries({ queryKey: bookletKeys.all });
    },
  });
}

// Reorder spots in booklet
export function useReorderBookletSpots(bookletId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spotIds: string[]) => {
      const response = await apiClient.patch<{ success: boolean }>(
        `/api/mekke/booklets/${bookletId}/spots/reorder`,
        { spotIds }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to reorder spots");
      }
      return spotIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.detail(bookletId) });
    },
  });
}

// Update booklet spot comment
interface UpdateBookletSpotInput {
  spotId: string;
  comment: string | null;
}

export function useUpdateBookletSpot(bookletId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateBookletSpotInput) => {
      const response = await apiClient.patch<MekkeBookletSpotDto>(
        `/api/mekke/booklets/${bookletId}/spots/${input.spotId}`,
        { comment: input.comment }
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update spot");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookletKeys.detail(bookletId) });
    },
  });
}
